// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Fix sprint #447 round 2 — the DPA statuses a vendor record may be written
 * with, in one place. `CreateVendorSchema` / `UpdateVendorSchema` parse
 * against this array and `VendorsPage` offers it, so the type, the server and
 * the form cannot disagree again. They did: this type listed
 * `required_missing | draft | under_review`, which the server refused, and
 * lacked `not_started | in_progress`, which the server and the form used.
 */
export const VENDOR_DPA_STATUSES = [
    'not_started',
    'in_progress',
    'signed',
    'expired',
    'not_required',
];
/**
 * Values the old type declared that no route accepts. A record written by a
 * direct insert rather than through the vendor routes (the sample-data seeder
 * writes `under_review`) can still carry one, so a stored vendor is typed to
 * include them and the bundle keeps their labels; no route offers or accepts
 * them.
 */
export const LEGACY_VENDOR_DPA_STATUSES = ['required_missing', 'draft', 'under_review'];
/** Runtime vocabulary, most severe first; the type is derived from it. */
export const VENDOR_RISK_LEVELS = ['critical', 'high', 'medium', 'low'];
/* ── Transfer Mechanisms (Epic B.2) ──────────────────────── */
/*
 * Owner ruling OD42 (fix-sprint-20261003-0625 F28) — the seven-member GDPR
 * Chapter V union offered to every stack is deleted. The mechanism is a code
 * an installed framework declares (`transfer-mechanism.ts`).
 */
// Sprint 63.8.2 gate E12.5 remediation A — a value, so the route schema's
// `z.enum` is built from this list rather than a second hand-written copy.
export const TRANSFER_MECHANISM_STATUSES = [
    'active',
    'expired',
    'under_review',
    'not_in_place',
];
/** The step statuses a deletion-propagation execution records, in order. */
export const STEP_EXECUTION_STATUSES = [
    'pending',
    'in_progress',
    'completed',
    'blocked',
    'skipped',
];
/** The status an approval record holds once a person has approved it. Read by consumers in place of the literal. */
export const APPROVAL_STATUS_APPROVED = 'approved';
/* ── Governance Evidence Types (Extension A) ─────────────── */
/**
 * fix-sprint-20261004-63-8-2 F4 — the evidence types the governance screens send (the Evidence page's
 * options, `deletion_confirmation` from offboarding and playbooks, `other` from
 * approvals). A superset of the data-mapping `EvidenceType`, written to the same
 * store; the governance create route reads this list.
 */
export const GOVERNANCE_EVIDENCE_TYPES = [
    'dpa_agreement',
    'risk_assessment',
    'audit_report',
    'certification',
    'deletion_confirmation',
    'training_record',
    'incident_report',
    'document',
    'screenshot',
    'policy',
    'contract',
    'assessment',
    'correspondence',
    'other',
];
// ────────────────────────────────────────────────────────────────
// Sprint 41.5.0 G1 — Cross-Border Filing Lifecycle
// ────────────────────────────────────────────────────────────────
/**
 * State machine for a single cross-border transfer filing.
 * The DPO drafts → assesses → submits to MPS → records receipt →
 * acknowledges → archives. Rejections and explicit withdrawals are
 * also supported but are not on the happy path.
 *
 * Allowed transitions (enforced by CrossBorderFilingService):
 *   draft           → assessed | archived
 *   assessed        → pending_review | draft | archived
 *   pending_review  → ready_to_file | rejected | assessed
 *   ready_to_file   → submitted | pending_review
 *   submitted       → acknowledged | rejected
 *   acknowledged    → archived
 *   rejected        → draft | archived
 *   archived        → (terminal)
 */
export const CROSS_BORDER_FILING_STATUSES = [
    'draft',
    'assessed',
    'pending_review',
    'ready_to_file',
    'submitted',
    'acknowledged',
    'rejected',
    'archived',
];
/**
 * OQ-86-6 (owner, 2026-10-26) — a register that owns the filings made on some
 * installed forms, so Cross-Border Filings refuses to open a filing on them.
 *
 * Which forms is never a list here (R-46): the backend reads it from the
 * installed packs, as the owning register itself does. `register` is a
 * platform key; `page` is the owning register's page in this product.
 */
export const CROSS_BORDER_FILING_REGISTER_OWNERS = {
    customs_tax_dossiers: {
        register: 'customs_tax_dossiers',
        page: '/admin/logistics/tax-dossiers',
    },
};
/** The refusal code of a create on a fenced form (409, `details.owner_register` / `details.owner_page`). */
export const CROSS_BORDER_FILING_FORM_OWNED_ELSEWHERE = 'FILING_FORM_OWNED_BY_ANOTHER_REGISTER';
//# sourceMappingURL=governance.js.map